import Alert from '@amzn/meridian/alert';
import Box from '@amzn/meridian/box';
import Breadcrumb, { BreadcrumbGroup } from '@amzn/meridian/breadcrumb';
import Button from '@amzn/meridian/button';
import Heading from '@amzn/meridian/heading';
import Loader from '@amzn/meridian/loader';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import React, {
  useCallback,
  useContext,
  useEffect,
  useState,
  useMemo,
} from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import DataSource from '../../api';
import {
  CoachingFormData,
  CoachingFormInputData,
  DeliveredStatusObject,
  enumEmpty,
} from '../../api/CoachingForm/schema';
import { StateContext } from '../../GlobalStateStore';
import { EmptyProfile, Profile } from '../../Types';
import DataState from '../../Types/DataState';
import Page from '../../Types/Page';
import { getCurrentWeek } from '../../Util';
import { renderUrl, urls } from '../../config/Url';
import ConditionalTooltip from '../CoachingForm/FieldComponents/ConditionalTooltip';
import CoachingHistory from '../Dashboard/DashboardTables/CoachingHistory';
import Input from '@amzn/meridian/input';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { searchInCoaching } from '../Goals/helper';

const CoachingLoader = (
  <div style={{ textAlign: 'center' }}>
    <Loader type='circular' size='large' />
  </div>
);

interface CoachingHistoryProps {
  userId: string | undefined;
  week: string | null;
}
const CoachingHistoryTm: React.FC<CoachingHistoryProps> = ({
  userId,
  week,
}: CoachingHistoryProps): React.ReactElement => {
  const apiNaws = DataSource.getDataFeed('NAWS');

  const intl = useIntl();

  const globalState = useContext(StateContext);
  const { inspireOptions } = globalState.state;
  const { cscEntityPrefix } = inspireOptions.data;
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const coachingIdParam = searchParams.get('coachingID');
  const [profile, setProfile] = useState<Profile>(EmptyProfile);
  const [profileState, setProfileState] = useState(DataState.LOADING);
  const [coachingHistory, setCoachingHistory] = useState<
    Array<CoachingFormData>
  >([]);
  const [coachingHistoryState, setCoachingHistoryState] = useState(
    DataState.LOADING
  );
  const [generatingForm, setGeneratingForm] = useState(false);

  const [createState, setCreateState] = useState('');
  const [searchQuery, setSearchQuery] = useState(coachingIdParam ?? '');

  useEffect(() => {
    queryCoachingHistory();
  }, [inspireOptions.state, userId]);

  const queryCoachingHistory = useCallback(() => {
    if (inspireOptions.state === DataState.LOADED && userId) {
      const queryMigrationVariables = apiNaws.prepareQuery(
        week || getCurrentWeek(),
        userId,
        cscEntityPrefix
      );
      apiNaws.getProfileData(
        queryMigrationVariables,
        setProfile,
        setProfileState
      );

      const queryVariablesNaws = apiNaws.prepareQuery(
        week || getCurrentWeek(),
        userId || null,
        cscEntityPrefix
      );
      apiNaws.getCoachingHistory(
        queryVariablesNaws,
        true,
        setCoachingHistory,
        setCoachingHistoryState
      );
    }
  }, [inspireOptions.state, userId]);

  const filteredCoachingHistory = useMemo(() => {
    if (!searchQuery) {
      return coachingHistory;
    }

    return coachingHistory.filter(coaching =>
      searchInCoaching(coaching, searchQuery, intl)
    );
  }, [coachingHistory, searchQuery]);

  const navigateToCoachingForm = (): void => {
    navigate(renderUrl(`${urls.coachingForm}/${userId}`));
  };

  useEffect(() => {
    if (createState === DataState.LOADED) {
      navigateToCoachingForm();
    }
  }, [createState]);

  if (profileState === DataState.LOADING) {
    return CoachingLoader;
  }

  if (profileState === DataState.ERROR) {
    return (
      <Alert type='error'>
        <FormattedMessage
          id='cs_perfportal_error_reason_1'
          defaultMessage='Your credentials may be incorrect or inadequate.'
        />
      </Alert>
    );
  }

  // This will only check new coaching notes, (Legacy note draft checking is not needed)
  const hasDraft = (history: CoachingFormData[]): boolean => {
    return !!history.find(
      coaching =>
        'isDelivered' in coaching &&
        coaching.isDelivered === DeliveredStatusObject.DRAFT
    );
  };

  const messages = {
    hasDraft: intl.formatMessage({
      id: 'cs_perfportal_coaching_history_new_coaching_disabled',
      defaultMessage: 'You can only have one draft at a time',
    }),
  };

  const queryRecipient = apiNaws.prepareQuery(
    week || getCurrentWeek(),
    userId,
    cscEntityPrefix
  );

  async function handleFormCreation() {
    setGeneratingForm(true);

    // Empty Coaching Form Input
    const initialValues: CoachingFormInputData = {
      id: '',
      recipient: {
        userID: `${userId ? queryRecipient.userID : ''}`,
        userName: `${profile.user.userName}`,
      },
      coach: {
        userID: '',
        userName: '',
      },
      isDelivered: DeliveredStatusObject.DRAFT,
      contactReview: [],
      coachingDetails: [],
      coachingSession: {
        managerNotes: '',
        coachingTactic: enumEmpty,
        coachingMethod: enumEmpty,
        duration: enumEmpty,
        preparationTime: enumEmpty,
        coachingSummaryNotes: '',
      },
      otherReason: '',
      reason: enumEmpty,
    };

    await apiNaws.updateCoachingNote(initialValues, setCreateState);
    if (createState === DataState.LOADED) {
      navigateToCoachingForm();
    }
    setGeneratingForm(false);
  }

  return (
    <>
      <Box>
        <Row spacingInset='300 none 500 none'>
          <BreadcrumbGroup>
            <Breadcrumb href={renderUrl(urls.team)} onClick={navigate}>
              <FormattedMessage id='cs_perfportal_team' defaultMessage='Team' />
            </Breadcrumb>
            <Breadcrumb
              href={renderUrl(`${urls.dashboard}/${userId}`)}
              onClick={navigate}
            >
              {profile.user.displayName}
            </Breadcrumb>
            <Breadcrumb>
              <FormattedMessage
                id='cs_perfportal_coaching_notes'
                defaultMessage='Coaching notes'
              />
            </Breadcrumb>
          </BreadcrumbGroup>
        </Row>
        <Row widths={['grid-9', 'grid-3']}>
          <Heading level={1} type='h400'>
            <FormattedMessage
              id='cs_perfportal_coaching_heading_formatted'
              defaultMessage='Coaching for {0}'
              values={{ 0: profile.user.displayName }}
            />
          </Heading>

          {coachingHistory.length > 0 && (
            <Text alignment='right'>
              <ConditionalTooltip
                tooltip={hasDraft(coachingHistory) && messages.hasDraft}
              >
                <Button
                  data-csa-c-type='button'
                  data-csa-c-slot-id='coaching-notes-history-tm-button-slot'
                  data-csa-c-content-id='coaching-notes-history-tm-new-coaching-button'
                  data-csa-c-tag1='Dashboard'
                  data-csa-c-tag2='Coaching History'
                  disabled={generatingForm || hasDraft(coachingHistory)}
                  onClick={() => handleFormCreation()}
                  data-cy={'new-coaching-note'}
                >
                  {generatingForm ? (
                    <Loader type='circular' size='small' />
                  ) : (
                    <FormattedMessage
                      id='cs_perfportal_coaching_new_coaching'
                      defaultMessage='New coaching'
                    />
                  )}
                </Button>
              </ConditionalTooltip>
            </Text>
          )}
        </Row>
        {coachingHistoryState === DataState.LOADING && (
          <div style={{ textAlign: 'center' }}>
            <Loader type='circular' size='large' />
          </div>
        )}
        {coachingHistoryState === DataState.ERROR && (
          <Alert type='error'>
            <FormattedMessage
              id='cs_perfportal_error_reason_1'
              defaultMessage='Your credentials may be incorrect or inadequate.'
            />
          </Alert>
        )}
        {coachingHistoryState === DataState.LOADED && (
          <>
            <div style={{ marginTop: '40px' }}>
              <Box type='fill' spacingInset='400' width='100%'>
                <Input
                  type='text'
                  placeholder={intl.formatMessage({
                    id: 'cs_perfportal_contact_recom_searchCoachingHistory',
                    defaultMessage: 'Search for Coaching History',
                  })}
                  value={searchQuery}
                  onChange={setSearchQuery}
                  width='300px'
                  data-testid='search-query-input'
                />
              </Box>
            </div>
            <Row spacingInset='none none 400' spacing='none'>
              <CoachingHistory
                coachingHistory={filteredCoachingHistory}
                isTeam
                legacyNotes={[]}
                profile={profile}
                profileState={profileState}
                page={Page.TEAM}
                refreshCoachingHistory={queryCoachingHistory}
              />
            </Row>
          </>
        )}
      </Box>
    </>
  );
};

export default CoachingHistoryTm;
